import React, { Component } from "react";
import Button from "react-bootstrap/Button";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import AddService from "../components/AddService.tsx";
import ServiceDetail from "../components/ServiceDetail.tsx";
import ServiceApiService from "../services/service.service";
import UserService from "../services/user.service";
import type { CalendarDisplaySettings, ServiceItem } from "./models";

const localizer = momentLocalizer(moment);
const BigCalendar: React.ElementType = Calendar as unknown as React.ElementType;

type ServiceCalendarState = {
  servicesData: ServiceItem[];
  showAddService: boolean;
  showServiceDetail: boolean;
  selectedSlotStart: Date | null;
  currentService: ServiceItem | null;
  date: Date;
  companyShortName: string;
} & CalendarDisplaySettings;

const buildCalendarTime = (hour: number, isEnd = false) => {
  return new Date(1972, 0, 1, hour, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
};

type ServiceCalendarStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

const eventStyleGetter = (event: ServiceItem) => {
  const colors: Record<ServiceCalendarStatus, string> = {
    scheduled: "yellow",
    confirmed: "red",
    paid: "#87C54D",
    done: "#10A689",
    client_not_show_up: "black",
    canceled_rob: "#6c757d",
    canceled_client: "#6c757d",
    canceled_overtime: "#6c757d",
  };
  const status = event.status as ServiceCalendarStatus;
  return {
    style: {
      backgroundColor: colors[status] || "#2D9BF0",
      color: status === "scheduled" ? "black" : "white",
    },
  };
};

export default class ServiceCalendar extends Component<Record<string, never>, ServiceCalendarState> {
  interval?: ReturnType<typeof setInterval>;

  constructor(props: Record<string, never>) {
    super(props);
    this.state = {
      servicesData: [] as ServiceItem[],
      showAddService: false,
      showServiceDetail: false,
      selectedSlotStart: null,
      currentService: null as ServiceItem | null,
      date: new Date(),
      calendarStartHour: 8,
      calendarEndHour: 18,
      companyShortName: "RDS",
    };
  }

  componentDidMount() {
    this.loadCalendarSettings();
    this.getServices();
    this.interval = setInterval(() => this.getServices(), 15000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  loadCalendarSettings = async () => {
    try {
      const result = await UserService.getAccountSettings();
      this.setState({
        calendarStartHour: result.data.data?.calendarStartHour ?? 8,
        calendarEndHour: result.data.data?.calendarEndHour ?? 18,
        companyShortName: result.data.data?.companyShortName ?? "RDS",
      });
    } catch (error) {
      console.log(error);
    }
  };

  getServices = async (props?: Date) => {
    try {
      const date = props || this.state.date;
      const firstDay = moment(
        new Date(date.getFullYear(), date.getMonth(), -10)
      )
        .toISOString()
        .replace("Z", "");
      const lastDay = moment(
        new Date(date.getFullYear(), date.getMonth() + 1, 10)
      )
        .toISOString()
        .replace("Z", "");
      const result = await ServiceApiService.getServices(firstDay, lastDay);
      if (
        JSON.stringify(this.state.servicesData) !==
        JSON.stringify(result.data.data)
      ) {
        this.setState({ servicesData: result.data.data });
      }
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error
      ) {
        const apiError = error as { response?: { data?: unknown } };
        console.log(apiError.response?.data);
      }
    }
  };

  handleSelectSlot = ({ start }: { start: Date }) => {
    this.setState({ showAddService: true, selectedSlotStart: start });
  };

  openAddService = (start?: Date | null) => {
    this.setState({ showAddService: true, selectedSlotStart: start ?? null });
  };

  handleSelectEvent = (service: ServiceItem) => {
    this.setState({ showServiceDetail: true, currentService: service });
  };

  onServiceChanged = () => {
    this.getServices();
  };

  viewChanged = (date: Date) => {
    this.setState({ date });
    this.getServices(date);
  };

  formatTitle = (service: ServiceItem) => {
    const hour = moment(service.startDateTime).format("h a");
    if (service.client) {
      return `${hour}: ${service.client.clientName} (${service.durationHours}h)`;
    }
    return `${hour}: Service (${service.durationHours}h)`;
  };

  render() {
    const {
      servicesData,
      showAddService,
      showServiceDetail,
      selectedSlotStart,
      currentService,
      date,
      calendarStartHour,
      calendarEndHour,
      companyShortName,
    } = this.state;

    const calendarMin = buildCalendarTime(calendarStartHour);
    const calendarMax = buildCalendarTime(calendarEndHour, true);

    return (
      <>
        <div className="container">
          <h2 className="mb-3">{date.toDateString()}</h2>
          <BigCalendar
            views={["month", "week", "agenda"]}
            selectable
            longPressThreshold={10}
            localizer={localizer}
            date={date}
            defaultView="month"
            events={servicesData}
            eventPropGetter={eventStyleGetter}
            style={{ height: "85vh" }}
            min={calendarMin}
            max={calendarMax}
            scrollToTime={calendarMin}
            onSelectEvent={this.handleSelectEvent}
            onSelectSlot={this.handleSelectSlot}
            onNavigate={this.viewChanged}
            startAccessor={(service: ServiceItem) => new Date(service.startDateTime)}
            endAccessor={(service: ServiceItem) => new Date(service.endDateTime)}
            titleAccessor={this.formatTitle}
          />
        </div>
        <Button
          variant="primary"
          className="d-md-none position-fixed"
          style={{ right: 16, bottom: 16, zIndex: 1050 }}
          onClick={() => this.openAddService(new Date())}
        >
          New Service
        </Button>
        <AddService
          show={showAddService}
          onHide={() => this.setState({ showAddService: false })}
          defaultStartDate={selectedSlotStart}
          onServiceCreated={this.onServiceChanged}
        />
        <ServiceDetail
          show={showServiceDetail}
          onHide={() => this.setState({ showServiceDetail: false })}
          service={currentService}
          companyShortName={companyShortName}
          onServiceChange={this.onServiceChanged}
        />
      </>
    );
  }
}
